import {
  EXPIRED,
  mapApiLoiStatus,
  isLoiSigned,
  resolveLoiStatus,
} from 'v2/helpers/status/loi';

/**
 * Is this entry backed by an actual LOI document?
 *
 * The endpoint filters only on `tender.has_loi` and resolves `document_id`
 * from an optional lookup (`loi/v1.php:196`), so a recommendation on an
 * LOI-enabled package whose LOI has not been generated yet comes back with
 * `document_id: null`. Rendering those produces a row that reads as Draft but
 * has no document to act on - and several of them collide on the same
 * `loi-null` key. Drop them until the endpoint guarantees otherwise.
 */
const hasLoiDocument = (entry) =>
  entry?.document_id !== null && entry?.document_id !== undefined;

/**
 * Map one API entry onto the row contract LoiRow consumes.
 */
const toLoiRow = (entry, packageId = null) => {
  const { uid, rawLabel } = mapApiLoiStatus(entry.status);

  return {
    type: 'loi',
    // The generated LOI document id. The API does not expose the
    // `letter_of_intent` row id, so this is the only LOI identifier available.
    loi_id: entry.document_id ?? null,
    tender_id: packageId ? Number(packageId) : null,
    // Signed is the precondition for raising the final order. Whether one has
    // already been raised cannot be answered from this payload - see
    // hasFinalOrder(), which needs the package's order rows too.
    is_signed: isLoiSigned(entry.status),
    loi_number: entry.reference,
    status: uid,
    rawStatusLabel: rawLabel,
    expiry_date: entry.expiry_date ?? null,
    created_at: entry.date ?? null,
    value: entry.value ?? null,
    company: entry.company ?? null,
    subcontractor: entry.subcontractor ?? {},
    submitted_by: entry.submitted_by ?? null,
    transaction_id: entry.transaction_id ?? null,
    last_updated: entry.last_updated ?? null,
    // { total, signers, can_sign } - how many signatories the LOI document
    // has and how many have signed. Feeds the Pending Signature count.
    signatory: entry.signatory ?? null,
    assigned_approvers: entry.assigned_approvers ?? [],
    isLevel: Boolean(entry.isLevel),
    can_issue_order: Boolean(entry.can_issue_order),
  };
};
const buildGate = (rows) => {
  const byTransaction = new Set();
  const bySubcontractor = new Set();

  rows.forEach((row) => {
    // An Executed LOI unlocks the order; a later rejected one must not
    // re-block it, so this only ever adds.
    if (!row.can_issue_order) return;

    if (row.transaction_id !== null && row.transaction_id !== undefined) {
      byTransaction.add(String(row.transaction_id));
    }
    if (row.subcontractor?.id !== undefined && row.subcontractor?.id !== null) {
      bySubcontractor.add(String(row.subcontractor.id));
    }
  });

  return { byTransaction, bySubcontractor };
};

/**
 * @param {object} payload the raw `{ data: [{ package, entries }] }` response
 * @returns {Array<{packageId: string, label: string, hasLoi: boolean, rows: object[], gate: object}>}
 */
const selectLoiPackages = (payload) => {
  const packages = payload?.data;
  if (!Array.isArray(packages)) return [];

  return packages
    .filter((pkg) => pkg?.package?.id !== undefined)
    .map((pkg) => {
      const entries = Array.isArray(pkg.entries) ? pkg.entries : [];
      const packageId = String(pkg.package.id);
      const rows = entries
        .filter(hasLoiDocument)
        .map((entry) => toLoiRow(entry, packageId));

      return {
        packageId,
        label: pkg.package.label ?? '',
        hasLoi: Boolean(pkg.package.has_loi),
        rows,
        gate: buildGate(rows),
      };
    });
};

/**
 * Is there any LOI to show?
 *
 * The endpoint is the source of truth: a non-empty `data` array means the
 * project has LOI-enabled packages and the section renders; an empty one
 * means it is hidden. Nothing about the individual entries is inspected.
 *
 * @param {object} payload the raw `{ data: [...] }` response
 */
const hasLoiData = (payload) => Boolean(payload?.data?.length);

const selectLoiPackagesById = (payload) =>
  selectLoiPackages(payload).reduce((acc, pkg) => {
    acc[pkg.packageId] = pkg;
    return acc;
  }, {});

const isOrderVisible = (gate, orderEntry, enabled) => {
  if (!enabled || !gate) return true;

  const orderId = orderEntry?.order_id;
  if (orderId !== undefined && orderId !== null) {
    if (gate.byTransaction.has(String(orderId))) return true;
  }

  const subcontractorId = orderEntry?.subcontractor?.id;
  if (subcontractorId !== undefined && subcontractorId !== null) {
    if (gate.bySubcontractor.has(String(subcontractorId))) return true;
  }

  return false;
};

/**
 * Does an order already exist for the same commitment as this LOI?
 *
 * The API carries no LOI -> Order link, so this is inference, not a recorded
 * relationship: an order row for the same transaction (or failing that, the
 * same subcontractor) in the same package is treated as this LOI's final
 * order. It cannot distinguish an order raised from this LOI from one raised
 * any other way, which is the cost of keeping this frontend-only.
 *
 * @param {object} loiRow a row from `selectLoiPackages`
 * @param {object[]} orderEntries the package's rows from `state.order.list`
 */
const hasFinalOrder = (loiRow, orderEntries = []) =>
  orderEntries.some((order) => {
    if (
      loiRow?.transaction_id !== null &&
      loiRow?.transaction_id !== undefined &&
      order?.order_id !== null &&
      order?.order_id !== undefined
    ) {
      return String(order.order_id) === String(loiRow.transaction_id);
    }

    const loiSubcontractor = loiRow?.subcontractor?.id;
    const orderSubcontractor = order?.subcontractor?.id;
    if (loiSubcontractor == null || orderSubcontractor == null) return false;

    return String(orderSubcontractor) === String(loiSubcontractor);
  });

/**
 * Expiry is resolved here rather than read off the row, so an LOI whose expiry
 * date has passed counts as expired even before its stored status catches up -
 * the same rule LoiRow renders by.
 */
const isExpiredLoi = (loiRow) =>
  resolveLoiStatus(loiRow?.status, loiRow?.expiry_date) === EXPIRED;

/**
 * A final order may be raised once the LOI is signed or has expired - the two
 * states project_service also unlocks the order for (`loi/v1.php:209`) - and
 * no order exists for the same commitment yet.
 */
const canCreateFinalOrder = (loiRow, orderEntries = []) =>
  (Boolean(loiRow?.is_signed) || isExpiredLoi(loiRow)) &&
  !hasFinalOrder(loiRow, orderEntries);

export {
  hasLoiDocument,
  toLoiRow,
  buildGate,
  hasFinalOrder,
  canCreateFinalOrder,
  hasLoiData,
  selectLoiPackages,
  selectLoiPackagesById,
  isOrderVisible,
};
